import { useEffect, useRef, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import EmailCodeSetup, { type SignInSettings } from './EmailCodeSetup';
import { BrandMark, Button, Notice, headingClasses } from './AuthUi';

interface SignInSetupGuideProps {
  settings: SignInSettings;
  onFinish: () => void;
  onSignInAgain: () => void;
}

/** Full-page, optional walkthrough shown once after a password sign-in. */
export default function SignInSetupGuide({ settings, onFinish, onSignInAgain }: SignInSetupGuideProps) {
  const [choosing, setChoosing] = useState(false);
  const [neverAsk, setNeverAsk] = useState(false);
  const snooze = useMutation(api.passwordlessAuth.snoozeSetupGuide);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { if (choosing) headingRef.current?.focus(); }, [choosing]);

  // Never keep someone from their readings over this preference: go straight
  // on, and save it in the background. A save that is lost on a bad
  // connection just means the guide is offered again next time.
  const saveChoice = () => {
    snooze({ neverAsk }).catch(error => console.error('Unable to save sign-in setup preference', error));
    onFinish();
  };

  const option = (value: boolean, title: string, detail: string) => <label
    className={`flex cursor-pointer gap-3 rounded-[14px] border-[1.5px] bg-white p-4 text-left ${neverAsk === value ? 'border-asg-forest' : 'border-asg-line'}`}>
    <input type="radio" name="sign-in-setup-ask-again" checked={neverAsk === value} onChange={() => setNeverAsk(value)}
      className="mt-0.5 h-5 w-5 shrink-0 accent-asg-forest" />
    <span>
      <span className="block text-[17px] font-semibold text-asg-ink">{title}</span>
      <span className="text-[15px] text-asg-slate">{detail}</span>
    </span>
  </label>;

  return <div className="min-h-screen bg-asg-paper px-6 py-8 text-asg-ink sm:py-12">
    <main className="mx-auto w-full max-w-[480px] space-y-6">
      <BrandMark />
      {choosing ? <div className="space-y-4 text-center">
        <h1 ref={headingRef} tabIndex={-1} className={`${headingClasses.guide} focus:outline-none`}>No problem</h1>
        <fieldset className="space-y-3">
          <legend className="mb-3 w-full text-center text-[17px]">Should we ask you again?</legend>
          {option(false, 'Remind me next time', "We'll ask again in a month")}
          {option(true, "Don't ask me again", 'You can still turn it on yourself')}
        </fieldset>
        <Notice tone="info">You can turn this on any time under <strong>Sign-in options</strong> on your dashboard.</Notice>
        <Button onClick={saveChoice}>Go to my readings</Button>
        <Button variant="quiet" onClick={() => setChoosing(false)}>Go back</Button>
      </div> : null}
      {/* Hidden rather than unmounted, so "Go back" returns to the same step. */}
      <div hidden={choosing}>
        <EmailCodeSetup variant="guide" settings={settings}
          onNotNow={() => setChoosing(true)} onDone={onFinish} onSignInAgain={onSignInAgain} />
      </div>
    </main>
  </div>;
}
